import { clamp, hash01 } from '@/core/rng';
import { DAY } from '@/sim/clock';
import type { EventBus } from '@/sim/events';
import {
  SOCIAL_CELL_SIZE, socialCenter, socialKey, socialNeighbors, type SocialCellKey,
} from '@/sim/social/socialGrid';
import { WILDLIFE_ARCHETYPES, WILDLIFE_SPECIES, type WildlifeCellState } from './types';

/**
 * Daily migration: herds do not sit on a collapsing K and starve in place.
 *
 * A species leaves when its own cell has visibly declined, or when a
 * neighbour's headroom — K per head standing — is substantially better than
 * home. Fire displacement emerges from exactly this rule: a burn collapses a
 * cell's K, the ratio craters, and within days the survivors walk out to
 * whichever stocked neighbour reads best.
 *
 * All decisions are made from a pre-move snapshot and applied afterwards, so
 * a cell's choice can never depend on whether its neighbour was processed
 * first. The moved fraction is hashed from (cell, species, day, seed) — no
 * dice that a different call pattern could reroll.
 */

/** Half a head of slack keeps the headroom ratio finite on empty cells. */
const RATIO_EPS = 0.5;
/** A neighbour must be this much better to be worth the walk. */
export const BETTER = 1.4;
/** "Habitat declined": today's K under this fraction of yesterday's. */
export const DECLINED = 0.7;
/** Below this there is no herd to move, only stragglers. */
export const MIN_HERD = 2;
/** The moved fraction is drawn from this band. */
const FRAC_LO = 0.15, FRAC_HI = 0.30;
/**
 * …and a press moves it inside this band: a whole hold (−1) still lets a
 * twentieth walk when the bar is met, and a whole press (+1) doubles the draw
 * and never past six tenths — a herd leaned on walks out over days, not in
 * one. Design numbers to be measured (`PRESS_PER_CAST` in populations.ts).
 */
const FRAC_MIN = 0.05, FRAC_MAX = 0.6;
/**
 * A cell receives at most this multiple of its own headroom (K − pop) in one
 * day, shared pro rata among everything walking in. Every source decides from
 * the same pre-move snapshot — the property that keeps the pass
 * order-independent — so on the day a burn turns to grass all eight
 * neighbours read the empty cell as eight times better and ALL send at once:
 * measured (`tools/trajectory-burn.ts`, before this), 45 browsers arrived on
 * a cell that carried 3.5 and died back over the following weeks, stripping
 * its range as they went. The 1.25 leaves room for a genuine flight to
 * crowd a full neighbour rather than stay on burning ground.
 */
const FILL = 1.25;

/**
 * Salience at the archetype's `greatMove` — the head count that makes a
 * migration of that kind great. Both gates that let the world SPEAK about one
 * sit at 0.7 (`ui/hud.ts`'s line, `band.ts`'s GREAT_MIGRATION), so the curve
 * is written to arrive there exactly at `greatMove` and the two halves of the
 * decision stay legible together. A move twice that size is not twice as
 * remarkable, so it runs on into the same 0.85 ceiling it always had.
 *
 * This replaced `0.08 + moved / 80` — a raw head count against the same 0.7
 * gate, i.e. 49.6 head for every animal alive. See `greatMove` in `types.ts`
 * for what that cost and why neither obvious denominator works.
 */
const SALIENCE_FLOOR = 0.08;
const SALIENCE_AT_GREAT = 0.7;
/**
 * A SPILL — the branch where nothing declined and a neighbour merely read
 * better — counts for this much of a FLIGHT of the same size. Measured on
 * the shipped century (`tools/trajectory-migrations.ts`, ROADMAP §42): 27
 * bands hunt the cells around their camps, a hunted cell reads 1.4× better
 * to its neighbours, and a few head spill in every day — 22,000 grazer
 * migrations a year, 64% of them touching a camp's ground, and 27% clearing
 * 0.7 under the flat curve. That is a hunting SINK, not "the herds moved on",
 * and it made the Chronicle beside a camp 94% herd lines (Lane D,
 * tools/tempo.ts). A spill has to be twice a great move to be spoken of; a
 * flight from collapsing ground — fire, drought, a hard Weaken — is the move
 * the sentence was written for and keeps the whole curve.
 */
const SPILL_WEIGHT = 0.5;

/** Headroom: how much K each standing head enjoys. High means room to grow. */
const headroom = (c: WildlifeCellState, s: (typeof WILDLIFE_SPECIES)[number]) =>
  (c.cap[s] + RATIO_EPS) / (c.pop[s] + RATIO_EPS);

/**
 * A herd's INCLINATION to leave its ground — the migration predicate, as a
 * pure read.
 *
 * It is one function rather than a line inside `migrationPass` for the same
 * reason `graspableAt` is one: the thing that decides whether a herd walks
 * out today, the card that says *they are inclined to move*, and the
 * Strengthen that leans on that inclination all have to be reading the same
 * comparison, or the quote and the bill name different herds. Nothing here
 * writes (law 9); `test/beasts.ts` advances a read world against an unread
 * one.
 *
 * `gradient` is how much better the best neighbour reads than home, as a
 * fraction of what the pass demands: 0 is *no better ground anywhere*, 1 is
 * *at the bar* — the herd walks today. Between the two the herd is inclined
 * and has not committed, which is exactly the state a verb can honestly lean
 * on: a real tendency, short of the threshold, and the world still deciding.
 */
export interface Inclination {
  /** Headroom at home. */
  here: number;
  /** The neighbour that reads best, if any reads better than home. */
  best: WildlifeCellState | null;
  bestR: number;
  /** Today's K has fallen under DECLINED of yesterday's — a flight, not a spill. */
  declined: boolean;
  /** (bestR / here − 1) / (BETTER − 1), clamped at 0 — the herd's OWN inclination, before any hand; 1 and above walks unpressed. */
  gradient: number;
  /** What is leaning on this herd, signed; 0 for nothing (`WildlifeCellState.press`). */
  pressed: number;
  /** The bar the pass actually holds them to today — BETTER, leaned on by the press. */
  bar: number;
  /** Would the pass move them today, at that bar? */
  would: boolean;
}

/**
 * The bar a pressed herd walks at. A whole press (+1) takes the bar to 1 —
 * any better neighbour suffices; a whole hold (−1) raises it to 1.8. The
 * flight branch (`declined`) is NOT leaned on: a herd whose ground has failed
 * under it walks whatever hand is holding it, because a Weaken cannot make a
 * burn feed anybody. The world resolves the outcome.
 */
export const barFor = (pressed: number): number =>
  1 + (BETTER - 1) * (1 - clamp(pressed, -1, 1));

export function inclinationOf(
  cells: ReadonlyMap<SocialCellKey, WildlifeCellState>,
  cell: WildlifeCellState,
  s: (typeof WILDLIFE_SPECIES)[number],
): Inclination {
  const here = headroom(cell, s);
  let best: WildlifeCellState | null = null;
  let bestR = here;
  for (const nb of socialNeighbors(cell.sx, cell.sz)) {
    const n = cells.get(socialKey(nb.sx, nb.sz));
    if (!n) continue;   // off the stocked map: nowhere to go
    const r = headroom(n, s);
    if (r > bestR) { bestR = r; best = n; }
  }
  const declined = cell.cap[s] < cell.prevCap[s] * DECLINED;
  const gradient = best ? Math.max(0, (bestR / here - 1) / (BETTER - 1)) : 0;
  const pressed = cell.press?.[s]?.movement?.v ?? 0;
  const bar = barFor(pressed);
  const would = !!best && cell.pop[s] >= MIN_HERD
    && (bestR > here * bar || (declined && bestR > here));
  return { here, best, bestR, declined, gradient, pressed, bar, would };
}

/**
 * One migration sweep over every stocked cell, at the day boundary
 * `dayIndex * DAY`. Mutates populations in place and emits one
 * 'wildlife_migrated' event per herd that moved.
 */
export function migrationPass(
  cells: ReadonlyMap<SocialCellKey, WildlifeCellState>,
  dayIndex: number,
  seed: number,
  events: EventBus,
): void {
  interface Move {
    from: WildlifeCellState; to: WildlifeCellState;
    species: (typeof WILDLIFE_SPECIES)[number]; moved: number; fraction: number;
    /** True when the herd left ground whose K had collapsed under it — a flight, not a spill. */
    fled: boolean;
  }
  const moves: Move[] = [];

  for (const cell of cells.values()) {
    for (let si = 0; si < WILDLIFE_SPECIES.length; si++) {
      const s = WILDLIFE_SPECIES[si];
      const pop = cell.pop[s];
      if (pop < MIN_HERD) continue;

      const inc = inclinationOf(cells, cell, s);
      if (!inc.best || !inc.would) continue;

      // …and a leaned-on herd moves more of itself: the drawn fraction reads
      // the press, inside a band that keeps a hold from freezing them and a
      // press from emptying a cell in a day (Beasts, *Migration Thread*)
      const fraction = clamp((FRAC_LO + (FRAC_HI - FRAC_LO) *
        hash01(cell.sx * 131 + si, cell.sz, seed + dayIndex * 0x9e37))
        * (1 + clamp(inc.pressed, -1, 1)), FRAC_MIN, FRAC_MAX);
      moves.push({ from: cell, to: inc.best, species: s, moved: pop * fraction, fraction, fled: inc.declined });
    }
  }

  // share each destination's headroom among its arrivals, from the pre-move
  // snapshot, so the scale a move gets cannot depend on which was applied first
  const inflow = new Map<WildlifeCellState, Partial<Record<(typeof WILDLIFE_SPECIES)[number], number>>>();
  for (const m of moves) {
    let by = inflow.get(m.to);
    if (!by) { by = {}; inflow.set(m.to, by); }
    by[m.species] = (by[m.species] ?? 0) + m.moved;
  }
  for (const m of moves) {
    const total = inflow.get(m.to)![m.species]!;
    const room = Math.max(0, m.to.cap[m.species] * FILL - m.to.pop[m.species]);
    if (total > room) {
      const scale = room / total;
      m.moved *= scale;
      m.fraction *= scale;
    }
  }

  for (const m of moves) {
    if (m.moved <= 0) continue;
    m.from.pop[m.species] -= m.moved;
    m.to.pop[m.species] += m.moved;
    const c = socialCenter(m.from.sx, m.from.sz);
    events.emit({
      type: 'wildlife_migrated',
      time: dayIndex * DAY,
      x: c.x, z: c.z,
      // the herd crossed into an adjacent cell: the whole move fits in this reach
      radius: SOCIAL_CELL_SIZE,
      magnitude: m.fraction,
      // a great herd on the move demands more explanation than a few
      // stragglers — sized against what a great move is for ITS OWN kind
      salience: clamp(
        SALIENCE_FLOOR + (SALIENCE_AT_GREAT - SALIENCE_FLOOR)
          * (m.moved / WILDLIFE_ARCHETYPES[m.species].greatMove)
          * (m.fled ? 1 : SPILL_WEIGHT),
        0, 0.85),
      cause: { kind: 'natural' },
      data: { species: m.species, moved: m.moved, toSx: m.to.sx, toSz: m.to.sz, fled: m.fled },
    });
  }
}
